/* eslint-disable import/no-deprecated */
import * as O from 'fp-ts/Option'
import { getObjectSemigroup } from 'fp-ts/Semigroup'
import { pipe } from 'fp-ts/function'
import type { FormWorker } from './FormWorker'
import type { FieldsValue } from './formBuilder'
import type { Validator } from './validators/Validator'

export function listToMapName<c extends FormWorker.StateFieldsUnion>(c: c, f: FormWorker.StateFieldUnion) {
    return { ...c, [f.name]: f }
}

export function mapFields<T extends FormWorker.StateFieldsUnion>(
    fields: T,
    map: (field: T[keyof T]) => typeof field
): T {
    return Object.values(fields)
        .map(map)
        .reduce(listToMapName, {} as T)
}

export const addNameToUnitForm = (unitsForm: FormWorker.Props['unitsForm']): FormWorker.formUnitNamized[] =>
    Object.keys(unitsForm).map(k => ({ name: k, ...unitsForm[k] }))

export const getFieldsToReset = (resetOnChange: FormWorker.Props['resetOnChange'], name: string): string[] =>
    pipe(
        O.fromNullable(resetOnChange),
        O.chainNullableK(roc => roc.filter(r => r.observed === name)[0]),
        O.map(r => r.list),
        O.getOrElse((): string[] => [])
    )

export const getValidetors = (
    name: string,
    unitsForm: FormWorker.Props['unitsForm'],
    formValidators: FormWorker.Props['validators'],
    onlyMultiple: boolean
) => {
    const fieldValidators = onlyMultiple
        ? O.none
        : pipe(
              O.fromNullable(unitsForm[name]),
              O.chainNullableK(u => u.validators)
          )

    const fieldFormValidators = pipe(
        O.fromNullable(formValidators),
        O.map(fvs =>
            Object.keys(fvs)
                .filter(vName => fvs[vName].showErrorOn === name)
                .reduce(
                    (c, vName) => ({ ...c, [vName]: fvs[vName] }),
                    {} as {
                        [k: string]: Validator.validatorMultipleBuilded<FieldsValue, any>
                    }
                )
        )
    )

    return pipe(
        O.getMonoid(getObjectSemigroup<Validator.validatorsBuilded>()).concat(fieldValidators, fieldFormValidators),
        O.toUndefined
    )
}

export const retriveAllTags = (fields: FormWorker.InternalValueFields): string[] =>
    Object.keys(Object.values(fields).reduce((c, f) => f.tags.reduce((ct, t) => ({ ...ct, [t]: true }), c), {}))

export const fieldsMerger = (
    valueFields: FormWorker.InternalValueFields,
    validatorFields: FormWorker.InternalValidatorFields
): FormWorker.InternalStateFields =>
    Object.keys(valueFields)
        .map(k => ({
            ...valueFields[k],
            ...pipe(
                O.fromNullable(validatorFields[k]),
                O.getOrElse(() => ({ isValidating: false, isValid: 'unknown', name: k }))
            ),
        }))
        .reduce(listToMapName, {} as FormWorker.InternalStateFields)
